import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  computed,
  effect,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { debounceTime } from 'rxjs';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import type { EChartsOption } from 'echarts';
import katex from 'katex';

import {
  CHART_BAR_BORDER_RADIUS,
  CHART_COLORS,
  CHART_GRID,
  CHART_TOOLTIP_STYLE,
  CHART_X_AXIS_STYLE,
  CHART_Y_AXIS_STYLE,
} from '../../../shared/chart-shared/chart-styling.util';
import { EchartsChartComponent } from '../../../shared/echarts-chart/echarts-chart.component';
import { binomialPmf } from '../../../statistics/tests/test-runner/distributions.util';
import { OutcomeSvgComponent } from './outcome-svg.component';
import { OUTCOME_SVG_MAP } from './outcome-svg.data';
import { ExperimentRunLogComponent } from './experiment-run-log.component';
import {
  ALL_EXPERIMENTS,
  ExperimentRun,
  OutcomeDefinition,
  ProbabilityExperimentDefinition,
  ProbabilityExperimentType,
  ExperimentTrialResult,
} from './probability-experiment.model';

const MAX_RUNS = 50;

const COLOR_N = '#f97316'; // orange-500
const COLOR_P = '#0ea5e9'; // sky-500
const COLOR_MU = '#8b5cf6'; // purple-500
const COLOR_SUCCESS = '#16a34a'; // green-600

function renderLatex(latex: string): string {
  return katex.renderToString(latex, { throwOnError: false, output: 'html', trust: true });
}

function normalPdf(x: number, mu: number, sigma: number): number {
  return Math.exp(-0.5 * ((x - mu) / sigma) ** 2) / (sigma * Math.sqrt(2 * Math.PI));
}

@Component({
  selector: 'dojo-probability-experiment',
  standalone: true,
  imports: [
    MatCheckbox,
    MatTabsModule,
    MatTooltipModule,
    ButtonComponent,
    IconComponent,
    OutcomeSvgComponent,
    EchartsChartComponent,
    ExperimentRunLogComponent,
  ],
  templateUrl: './probability-experiment.component.html',
  styleUrl: './probability-experiment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'block' },
})
export class ProbabilityExperimentComponent {
  constructor(private readonly sanitizer: DomSanitizer) {
    effect(() => {
      const type = this.initialExperiment();
      this.selectedType.set(type);
      const count = this.experiments.find((e) => e.type === type)!.outcomes.length;
      this.targetOutcomes.set(Array.from({ length: count }, (_, i) => i === 0));
    });

    effect(() => {
      const k = this.hoveredK();
      const chart = this.empiricalChartInstance;
      if (!chart) {
        return;
      }

      if (this.lastHighlightedK !== null) {
        chart.dispatchAction({ type: 'downplay', seriesIndex: 0, dataIndex: this.lastHighlightedK });
      }
      if (k !== null) {
        chart.dispatchAction({ type: 'highlight', seriesIndex: 0, dataIndex: k });
      }
      this.lastHighlightedK = k;
    });
  }

  private readonly runsScrollContainer = viewChild<ElementRef<HTMLElement>>('runsScrollContainer');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private empiricalChartInstance: any = null;
  private lastHighlightedK: number | null = null;

  readonly initialExperiment = input<ProbabilityExperimentType>('coin');
  readonly experiments = ALL_EXPERIMENTS;
  readonly selectedType = signal<ProbabilityExperimentType>('coin');

  readonly selectedTabIndex = computed(() => {
    return this.experiments.findIndex((e) => e.type === this.selectedType());
  });

  readonly selectedExperiment = computed(() => {
    return this.experiments.find((e) => e.type === this.selectedType())!;
  });

  readonly targetOutcomes = signal<boolean[]>([true, false]);

  readonly trials = signal(30);
  readonly runs = signal<ExperimentRun[]>([]);
  readonly hoveredK = signal<number | null>(null);
  readonly isTrialsLocked = computed(() => this.runs().length > 0);
  readonly maxRunsReached = computed(() => this.runs().length >= MAX_RUNS);

  readonly xHtml = renderLatex('X');
  readonly pxHtml = renderLatex('P(X)');
  readonly uHtml = renderLatex('U');
  readonly aHtml = renderLatex('A');
  readonly nColoredHtml = renderLatex(`\\textcolor{${COLOR_N}}{n}`);
  readonly kLabelHtml = renderLatex('k');
  readonly pxkLabelHtml = renderLatex('P(X\\!=\\!k)');
  readonly muLabelHtml = renderLatex(`\\textcolor{${COLOR_MU}}{\\mu}`);

  readonly setupNarrativeHtml = computed<SafeHtml>(() => {
    const exp = this.selectedExperiment();
    const svg = (type: string) => this.getOutcomeSvgMarkup(type);
    let html = '';

    switch (exp.type) {
      case 'coin':
        html =
          `You are flipping a fair coin. There are two equally likely outcomes: ` +
          `${svg('coin-heads')}\u00A0Heads and ${svg('coin-tails')}\u00A0Tails, ` +
          `each with probability ${renderLatex('\\frac{1}{2}')}. ` +
          `This is a discrete uniform distribution.`;
        break;
      case 'die':
        html =
          `You are rolling a fair six-sided die. The numbers ` +
          `${svg('die-1')}\u00A0${svg('die-2')}\u00A0${svg('die-3')}\u00A0${svg('die-4')}\u00A0${svg(
            'die-5',
          )}\u00A0${svg('die-6')} ` +
          `each appear with probability ${renderLatex('\\frac{1}{6}')}. ` +
          `This is a discrete uniform distribution because all outcomes are equally likely.`;
        break;
      case 'basketball':
        html =
          `You are shooting a free throw with a ${renderLatex('\\frac{3}{4}')} chance of making it ${svg(
            'basketball-make',
          )}. ` + `Unlike the coin or die, the outcomes are not equally likely.`;
        break;
      case 'lottery':
        html =
          `You are playing a lottery ${svg('lottery-win')}. You pay $2 for a ${renderLatex(
            '\\frac{1}{10}',
          )} chance to win $20 (net +$18). ` +
          `The expected value is ${renderLatex(
            'E(X) = \\frac{1}{10} \\times 18 + \\frac{9}{10} \\times (-2) = 0',
          )}, making this a <strong>fair game</strong>: ` +
          `on average, you neither gain nor lose money over many plays.`;
        break;
    }

    return this.sanitizer.bypassSecurityTrustHtml(html);
  });

  readonly binsListHtml = computed<SafeHtml>(() => {
    const p = this.successProbability();
    if (p <= 0 || p >= 1) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    const exp = this.selectedExperiment();
    const selected = this.targetOutcomes();
    const successSvgs = exp.outcomes
      .filter((_, i) => selected[i])
      .map((o) => this.getOutcomeSvgMarkup(o.svgType))
      .join('\u00A0');
    const eventDesc = this.buildEventDescription(exp, selected);
    const eventLabel = eventDesc ? ` (${eventDesc})` : '';
    const n = this.trials();
    const nC = renderLatex(`\\textcolor{${COLOR_N}}{${n}}`);
    const pFrac = this.pFracLatex();
    const pLabel = renderLatex(`P(A) = ${pFrac}`);
    const verb = this.getExperimentVerb();
    const noun = this.getExperimentNoun();

    const check = '<span class="bins-check">&#10003;</span>';

    const html =
      `<p class="bins-list-intro">This is because it meets all four <strong>BINS</strong> conditions:</p>` +
      `<ol class="bins-ordered-list">` +
      `<li><strong>B</strong>inary outcomes: you've defined success as ${successSvgs}${eventLabel}. Each trial results in either success or failure. ${check}</li>` +
      `<li><strong>I</strong>ndependent trials: ${verb.toLowerCase()} the ${noun} again doesn't change the probabilities. ${check}</li>` +
      `<li>Fixed <strong>N</strong>umber of trials: we'll repeat this experiment ${nC} times. ${check}</li>` +
      `<li><strong>S</strong>ame probability: the probability of success is ${pLabel} on every trial. ${check}</li>` +
      `</ol>`;

    return this.sanitizer.bypassSecurityTrustHtml(html);
  });

  readonly fractionLabels = computed<SafeHtml[]>(() => {
    const selected = this.targetOutcomes();
    return this.selectedExperiment().outcomes.map((o, i) => {
      const frac = `${o.probabilityNumerator}/${o.probabilityDenominator}`;
      const html = selected[i] ? renderLatex(`\\textcolor{${COLOR_P}}{${frac}}`) : renderLatex(frac);
      return this.sanitizer.bypassSecurityTrustHtml(html);
    });
  });

  readonly valueTagHtmls = computed(() => {
    return this.selectedExperiment().outcomes.map((o) => renderLatex(`X = ${o.value}`));
  });

  readonly hasNonNumericLabels = computed(() => {
    return this.selectedExperiment().outcomes.some((o) => o.label !== String(o.value));
  });

  readonly isUniform = computed(() => {
    const outcomes = this.selectedExperiment().outcomes;
    const first = outcomes[0];
    return outcomes.every(
      (o) =>
        o.probabilityNumerator === first.probabilityNumerator &&
        o.probabilityDenominator === first.probabilityDenominator,
    );
  });

  readonly distributionTooltip =
    'Each outcome is equally likely. The probability is spread uniformly across all values in the sample space.';
  readonly trialsLockedTooltip = 'Reset your experiments to change the number of trials.';

  readonly successProbabilityLabel = computed(() => {
    const outcomes = this.selectedExperiment().outcomes;
    const selected = this.targetOutcomes();
    let numSum = 0;
    const den = outcomes[0].probabilityDenominator;
    for (let i = 0; i < outcomes.length; i++) {
      if (selected[i]) {
        numSum += outcomes[i].probabilityNumerator;
      }
    }
    if (numSum === 0) {
      return '0';
    }
    if (numSum === den) {
      return '1';
    }
    return `${numSum}/${den}`;
  });

  readonly successProbabilityHtml = computed<SafeHtml>(() => {
    const label = this.successProbabilityLabel();
    const colored = label === '0' || label === '1' ? label : `\\textcolor{${COLOR_P}}{${label}}`;
    return this.sanitizer.bypassSecurityTrustHtml(`Probability of success ${renderLatex(`P(A) = ${colored}`)}`);
  });

  readonly successDescription = computed(() => {
    const exp = this.selectedExperiment();
    const selected = this.targetOutcomes();
    const selectedValues = exp.outcomes.filter((_, i) => selected[i]).map((o) => o.value);

    if (selectedValues.length === 0) {
      return 'Select at least one outcome to define event A.';
    }

    const set = `A = {${selectedValues.join(', ')}}`;

    if (selectedValues.length === exp.outcomes.length) {
      return `${set}, all outcomes selected, success is certain.`;
    }

    const desc = this.buildEventDescription(exp, selected);
    return desc ? `${set}, ${desc}` : set;
  });

  readonly results = signal<ExperimentTrialResult[]>([]);
  readonly visibleCount = signal(0);
  readonly visibleResults = computed(() => {
    const sliced = this.results().slice(0, this.visibleCount());
    const exp = this.selectedExperiment();
    const selected = this.targetOutcomes();
    return sliced.map((r) => ({
      ...r,
      isSuccess: selected[exp.outcomes.indexOf(r.outcome)] ?? false,
    }));
  });

  readonly hasSelectedOutcomes = computed(() => this.targetOutcomes().some(Boolean));

  readonly summaryStats = computed(() => {
    const results = this.visibleResults();
    const total = results.length;
    const successCount = results.filter((r) => r.isSuccess).length;
    const lossCount = total - successCount;
    return {
      total,
      successCount,
      lossCount,
      successProb: total > 0 ? successCount / total : 0,
      lossProb: total > 0 ? lossCount / total : 0,
    };
  });

  readonly successSvgTypes = computed(() => {
    const exp = this.selectedExperiment();
    const selected = this.targetOutcomes();
    return exp.outcomes.filter((_, i) => selected[i]).map((o) => o.svgType);
  });

  readonly successProbability = computed(() => {
    const outcomes = this.selectedExperiment().outcomes;
    const selected = this.targetOutcomes();
    let numSum = 0;
    const den = outcomes[0].probabilityDenominator;
    for (let i = 0; i < outcomes.length; i++) {
      if (selected[i]) {
        numSum += outcomes[i].probabilityNumerator;
      }
    }
    return numSum / den;
  });

  readonly comparisonStats = computed(() => {
    const n = this.summaryStats().total;
    const p = this.successProbability();
    const observed = this.summaryStats().successCount;
    const expected = n * p;
    const sigma = Math.sqrt(n * p * (1 - p));
    const delta = observed - expected;
    const z = sigma > 0 ? delta / sigma : 0;
    return { expected, observed, sigma, delta, z };
  });

  readonly runExperimentNarrativeHtml = computed<SafeHtml>(() => {
    const n = this.trials();
    const p = this.successProbability();
    const mu = (n * p).toFixed(1);
    const verb = this.getBinomialVerb();
    const nC = renderLatex(`\\textcolor{${COLOR_N}}{n}`);
    const muC = renderLatex(`\\textcolor{${COLOR_MU}}{\\mu = ${mu}}`);
    const html =
      `We've established that over ${nC} trials, we expect ${muC} successes on average. ` +
      `Now let's put theory to the test. Click <strong>Run Experiment</strong> to <strong>${verb}</strong> and see how the results compare.`;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  });

  readonly binomialVerb = computed(() => this.getBinomialVerb());

  readonly successEventDescription = computed(() => {
    const exp = this.selectedExperiment();
    const selected = this.targetOutcomes();
    return this.buildEventDescription(exp, selected);
  });

  readonly pALatexHtml = computed<SafeHtml>(() => {
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(`P(A) = ${this.pFracLatex()}`));
  });

  readonly binomialDistEqHtml = computed<SafeHtml>(() => {
    const nC = `\\textcolor{${COLOR_N}}{${this.trials()}}`;
    return this.sanitizer.bypassSecurityTrustHtml(
      renderLatex(`X \\sim B\\!\\left(${nC},\\, ${this.pFracLatex()}\\right)`),
    );
  });

  readonly binomialPmfEqHtml = computed<SafeHtml>(() => {
    const nC = `\\textcolor{${COLOR_N}}{${this.trials()}}`;
    const p = this.pFracLatex();
    const pmf = `\\binom{${nC}}{k} \\cdot \\left(${p}\\right)^{k} \\cdot \\left(1 - ${p}\\right)^{${nC}-k}`;
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(`P(X = k) = ${pmf}, \\quad k = 0, 1, 2, \\ldots, ${nC}`));
  });

  readonly binomialExpectedMuHtml = computed<SafeHtml>(() => {
    const n = this.trials();
    const p = this.successProbability();
    const mu = (n * p).toFixed(1);
    const nC = `\\textcolor{${COLOR_N}}{${n}}`;
    const muC = `\\textcolor{${COLOR_MU}}{${mu}}`;
    return this.sanitizer.bypassSecurityTrustHtml(
      renderLatex(`E[X] = np = ${nC} \\times ${this.pFracLatex()} = ${muC}`),
    );
  });

  readonly binomialExpectedSigmaHtml = computed<SafeHtml>(() => {
    const n = this.trials();
    const p = this.successProbability();
    const sigma = Math.sqrt(n * p * (1 - p)).toFixed(2);
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(`\\sigma = \\sqrt{np(1\\!-\\!p)} \\approx ${sigma}`));
  });

  readonly binomialChartOptions = computed<EChartsOption>(() => {
    const n = this.trials();
    const p = this.successProbability();
    if (n === 0 || p === 0 || p === 1) {
      return {};
    }

    const mu = n * p;
    const sigma = Math.sqrt(n * p * (1 - p));

    const categories = Array.from({ length: n + 1 }, (_, k) => k);
    const pmfData = categories.map((k) => binomialPmf(k, n, p));

    const normalData: Array<[number, number]> = [];
    for (let x = 0; x <= n; x += 0.2) {
      normalData.push([x, normalPdf(x, mu, sigma)]);
    }

    return {
      tooltip: {
        ...CHART_TOOLTIP_STYLE,
        trigger: 'axis',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const bar = Array.isArray(params) ? params.find((item: any) => item.seriesType === 'bar') : params;
          if (!bar) {
            return '';
          }
          const k = bar.dataIndex;
          const prob = pmfData[k];
          return `<div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px"><span style="color:${
            CHART_COLORS.label
          }">${renderLatex(`k`)}</span><span style="font-weight:500;color:${
            CHART_COLORS.boldText
          }">${k}</span></div><div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px"><span style="color:${
            CHART_COLORS.label
          }">${renderLatex(`P(X = ${k})`)}</span><span style="font-weight:500;color:${
            CHART_COLORS.boldText
          }">${prob.toFixed(4)}</span></div>`;
        },
      },
      grid: { ...CHART_GRID, bottom: 30 },
      xAxis: [
        {
          ...CHART_X_AXIS_STYLE,
          type: 'category',
          data: categories,
          axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(0,0,0,0.04)' } },
        },
        { type: 'value', min: -0.5, max: n + 0.5, show: false, axisPointer: { show: false } },
      ],
      yAxis: { ...CHART_Y_AXIS_STYLE, type: 'value' },
      series: [
        {
          type: 'bar',
          xAxisIndex: 0,
          data: pmfData.map((v) => ({
            value: v,
            itemStyle: {
              color: '#d1d5db',
              borderRadius: CHART_BAR_BORDER_RADIUS,
            },
          })),
          barMaxWidth: 20,
        },
        {
          type: 'line',
          xAxisIndex: 1,
          data: normalData,
          smooth: true,
          symbol: 'none',
          lineStyle: { color: '#818cf8', width: 2, type: 'dashed' },
          tooltip: { show: false },
          markLine: {
            silent: true,
            data: [
              {
                xAxis: mu,
                lineStyle: { color: COLOR_MU, type: 'solid', width: 1.5 },
                label: {
                  show: true,
                  position: 'end',
                  formatter: 'μ',
                  color: COLOR_MU,
                  fontSize: 13,
                  fontStyle: 'italic',
                },
              },
              {
                xAxis: mu - sigma,
                lineStyle: { color: '#6b7280', type: 'dashed', width: 1, opacity: 0.4 },
                label: { show: true, position: 'end', formatter: '−σ', color: '#6b7280', fontSize: 10 },
              },
              {
                xAxis: mu + sigma,
                lineStyle: { color: '#6b7280', type: 'dashed', width: 1, opacity: 0.4 },
                label: { show: true, position: 'end', formatter: '+σ', color: '#6b7280', fontSize: 10 },
              },
            ],
            symbol: 'none',
          },
        },
      ],
    };
  });

  readonly empiricalDistributionChartOptions = computed<EChartsOption>(() => {
    const runs = this.runs();
    if (runs.length < 1) {
      return {};
    }

    const n = this.trials();
    const p = this.successProbability();
    if (n === 0 || p === 0 || p === 1) {
      return {};
    }

    const mu = n * p;
    const totalRuns = runs.length;

    // Count how many runs had k successes
    const counts = new Array<number>(n + 1).fill(0);
    for (const run of runs) {
      counts[run.successCount]++;
    }

    // Relative frequencies (empirical) and theoretical PMF
    const categories = Array.from({ length: n + 1 }, (_, k) => k);
    const empiricalFreq = counts.map((c) => c / totalRuns);
    const theoreticalPmf = categories.map((k) => binomialPmf(k, n, p));

    return {
      tooltip: {
        ...CHART_TOOLTIP_STYLE,
        trigger: 'axis',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) {
            return '';
          }
          const k = params[0].dataIndex;
          const empirical = empiricalFreq[k];
          const theoretical = theoreticalPmf[k];
          return (
            `<div style="margin-bottom:4px;font-weight:600;color:${CHART_COLORS.boldText}">${renderLatex(
              `k = ${k}`,
            )}</div>` +
            `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:${
              CHART_COLORS.label
            }">Empirical</span><span style="font-weight:500;color:${COLOR_SUCCESS}">${empirical.toFixed(
              4,
            )}</span></div>` +
            `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:${
              CHART_COLORS.label
            }">Theoretical</span><span style="font-weight:500;color:#9ca3af">${theoretical.toFixed(4)}</span></div>` +
            `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:${CHART_COLORS.label}">Experiments</span><span style="font-weight:500;color:${CHART_COLORS.boldText}">${counts[k]}</span></div>`
          );
        },
      },
      grid: { top: 48, bottom: 8, left: 12, right: 12, containLabel: true },
      xAxis: [
        {
          ...CHART_X_AXIS_STYLE,
          type: 'category',
          data: categories,
          axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(0,0,0,0.04)' } },
        },
        { type: 'value', min: -0.5, max: n + 0.5, show: false, axisPointer: { show: false } },
      ],
      yAxis: { ...CHART_Y_AXIS_STYLE, type: 'value' },
      series: [
        {
          name: 'Empirical',
          type: 'bar',
          xAxisIndex: 0,
          data: empiricalFreq.map((v) => ({
            value: v,
            itemStyle: {
              color: 'rgba(22, 163, 74, 0.5)',
              borderRadius: CHART_BAR_BORDER_RADIUS,
            },
          })),
          emphasis: {
            itemStyle: {
              color: COLOR_SUCCESS,
            },
          },
          barMaxWidth: 20,
        },
        {
          name: 'Theoretical',
          type: 'line',
          xAxisIndex: 0,
          data: theoreticalPmf,
          smooth: false,
          symbol: 'circle',
          symbolSize: 4,
          lineStyle: { color: '#9ca3af', width: 2 },
          itemStyle: { color: '#9ca3af' },
          z: 10,
        },
        {
          type: 'line',
          xAxisIndex: 1,
          data: [],
          tooltip: { show: false },
          markLine: {
            silent: true,
            data: [
              {
                xAxis: mu,
                lineStyle: { color: COLOR_MU, type: 'solid', width: 1.5 },
                label: {
                  show: true,
                  position: 'end',
                  formatter: 'μ',
                  color: COLOR_MU,
                  fontSize: 13,
                  fontStyle: 'italic',
                },
              },
            ],
            symbol: 'none',
          },
        },
      ],
    };
  });

  private readonly postExperimentNarrativeHtmlRaw = computed<SafeHtml>(() => {
    const runs = this.runs();
    if (runs.length < 1) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    const n = this.trials();
    const p = this.successProbability();
    const mu = n * p;
    const sigma = Math.sqrt(n * p * (1 - p));
    const totalRuns = runs.length;
    const latestRun = runs[runs.length - 1];
    const latestK = latestRun.successCount;

    let empiricalMeanSum = 0;
    let minK = Infinity;
    let maxK = -Infinity;
    for (const run of runs) {
      empiricalMeanSum += run.successCount;
      if (run.successCount < minK) {
        minK = run.successCount;
      }
      if (run.successCount > maxK) {
        maxK = run.successCount;
      }
    }
    const empiricalMean = empiricalMeanSum / totalRuns;
    const meanDiff = Math.abs(empiricalMean - mu);

    const delta = latestK - mu;
    const z = sigma > 0 ? delta / sigma : 0;
    const absZ = Math.abs(z);
    const direction = z >= 0 ? 'above' : 'below';
    const sign = z >= 0 ? '+' : '';

    const l = renderLatex;
    const muLabel = l(`\\textcolor{${COLOR_MU}}{\\mu = ${mu.toFixed(1)}}`);
    const xBar = l(`\\bar{x} = \\textcolor{${COLOR_SUCCESS}}{${empiricalMean.toFixed(2)}}`);
    let html = '';

    if (totalRuns === 1) {
      // First experiment: introduce the observed vs expected comparison, then explain the chart
      html +=
        `<p>Your first experiment produced ${l(`\\textcolor{${COLOR_SUCCESS}}{${latestK}}`)} successes out of ` +
        `${l(`\\textcolor{${COLOR_N}}{${n}}`)} trials, ` +
        `which is ${l(`z = ${sign}${z.toFixed(2)}`)} standard deviations <strong>${direction}</strong> ` +
        `the expected value ${muLabel}. `;

      if (absZ < 0.5) {
        html += `That's very close to what we predicted.`;
      } else if (absZ < 1) {
        html += `Well within one standard deviation, a completely ordinary result.`;
      } else if (absZ < 2) {
        html += `Between one and two standard deviations from the mean, still a typical result.`;
      } else {
        html += `A somewhat unusual result, but that's the nature of randomness.`;
      }

      html +=
        `</p><p>On the chart, this is the single green bar. The gray curve shows the theoretical binomial probability ` +
        `${l('P(X=k)')} for every possible ${l('k')} from 0 to ${l(`\\textcolor{${COLOR_N}}{${n}}`)}. ` +
        `If you run the experiment again, you'll likely get a different ${l('k')}. ` +
        `The binomial distribution tells us that values near ${muLabel} should appear most often, ` +
        `while extreme values should be rare. Run a few more experiments to test this.</p>`;
    } else if (totalRuns <= 5) {
      const kValues = runs.map((r) => r.successCount).join(', ');
      html +=
        `<p>The latest experiment gave ${l(`\\textcolor{${COLOR_SUCCESS}}{${latestK}}`)} successes ` +
        `(${l(`z = ${sign}${z.toFixed(2)}`)}, <strong>${direction}</strong> ${l(`\\mu`)}). ` +
        `Across your ${l(`\\textcolor{${COLOR_SUCCESS}}{${totalRuns}}`)} experiments so far ` +
        `(${l(`k = ${kValues}`)}), each result is different. ` +
        `This is expected: a binomial distribution doesn't predict a single outcome, ` +
        `it describes the <em>pattern</em> that emerges across many repetitions.</p>` +
        `<p>Notice the green bars on the chart clustering around ${muLabel} ` +
        `where the gray binomial curve peaks. ` +
        `The empirical mean is ${xBar}. ` +
        `With more experiments, this will converge toward ${l(`\\mu`)}.</p>`;
    } else if (totalRuns < 20) {
      html +=
        `<p>Experiment ${l(`\\textcolor{${COLOR_SUCCESS}}{${totalRuns}}`)} gave ` +
        `${l(`\\textcolor{${COLOR_SUCCESS}}{${latestK}}`)} successes ` +
        `(${l(`z = ${sign}${z.toFixed(2)}`)}).  ` +
        `Across all experiments, ${l('k')} has ranged from ` +
        `${l(`\\textcolor{${COLOR_SUCCESS}}{${minK}}`)} to ${l(`\\textcolor{${COLOR_SUCCESS}}{${maxK}}`)}. ` +
        `The green bars are taking shape and following the gray binomial curve: ` +
        `values near ${muLabel} come up most often, just as the formula predicted.</p>` +
        `<p>The empirical mean is ${xBar}, ` +
        `${meanDiff < 1 ? 'close to' : 'approaching'} ${l(`\\mu`)}. ` +
        `This is what connects theory to practice: the formula ${l('P(X=k)')} gives us the probability, ` +
        `and the relative frequency from experiments is converging to match it.</p>`;
    } else {
      html +=
        `<p>Over ${l(`\\textcolor{${COLOR_SUCCESS}}{${totalRuns}}`)} experiments, ` +
        `${l('k')} has ranged from ${l(`\\textcolor{${COLOR_SUCCESS}}{${minK}}`)} to ` +
        `${l(`\\textcolor{${COLOR_SUCCESS}}{${maxK}}`)} ` +
        `and the empirical mean is ${xBar}` +
        `${meanDiff < 0.5 ? `, very close to ${muLabel}` : `, converging toward ${muLabel}`}.</p>` +
        `<p>The green bars now closely match the gray binomial curve. ` +
        `This demonstrates an important principle: while any single experiment is unpredictable, ` +
        `the relative frequencies over many repetitions converge to the theoretical probabilities. ` +
        `In statistics, this is called the <strong>Law of Large Numbers</strong>.</p>`;
    }

    return this.sanitizer.bypassSecurityTrustHtml(html);
  });

  readonly postExperimentNarrativeHtml = toSignal(
    toObservable(this.postExperimentNarrativeHtmlRaw).pipe(debounceTime(50)),
    { initialValue: this.sanitizer.bypassSecurityTrustHtml('') },
  );

  onTabChange(index: number): void {
    this.selectExperiment(this.experiments[index].type);
  }

  selectExperiment(type: ProbabilityExperimentType): void {
    this.results.set([]);
    this.visibleCount.set(0);
    this.runs.set([]);
    this.selectedType.set(type);
    const count = this.experiments.find((e) => e.type === type)!.outcomes.length;
    this.targetOutcomes.set(Array.from({ length: count }, (_, i) => i === 0));
  }

  onEmpiricalChartInit(chart: unknown): void {
    this.empiricalChartInstance = chart;
  }

  toggleTargetOutcome(index: number): void {
    this.results.set([]);
    this.visibleCount.set(0);
    this.runs.set([]);
    this.targetOutcomes.update((prev) => prev.map((v, i) => (i === index ? !v : v)));
  }

  onTrialsChange(value: number): void {
    this.trials.set(value);
  }

  stepTrials(delta: number): void {
    const value = Math.min(200, Math.max(1, this.trials() + delta));
    this.trials.set(value);
  }

  onInlineTrialsChange(event: Event): void {
    const trialsInput = event.target as HTMLInputElement;
    let value = Math.round(Number(trialsInput.value));
    if (isNaN(value) || value < 1) {
      value = 1;
    }
    if (value > 200) {
      value = 200;
    }
    trialsInput.value = String(value);
    this.trials.set(value);
  }

  resetResults(): void {
    this.results.set([]);
    this.visibleCount.set(0);
    this.runs.set([]);
  }

  runBatchExperiments(count: number): void {
    for (let i = 0; i < count; i++) {
      if (this.maxRunsReached()) {
        break;
      }
      this.runSingleExperiment();
    }
    this.scrollRunsToBottom();
  }

  runExperiment(): void {
    this.runSingleExperiment();
  }

  private buildEventDescription(exp: ProbabilityExperimentDefinition, selected: boolean[]): string | null {
    const selectedLabels = exp.outcomes.filter((_, i) => selected[i]).map((o) => o.label);
    const selectedValues = exp.outcomes.filter((_, i) => selected[i]).map((o) => o.value);

    switch (exp.type) {
      case 'coin':
        return selectedLabels[0] === 'Heads' ? 'flipping Heads' : 'flipping Tails';

      case 'die': {
        const sorted = [...selectedValues].sort((a, b) => a - b);
        const count = sorted.length;
        if (count === 1) {
          return `rolling a ${sorted[0]}`;
        }
        const isEven = sorted.every((v) => v % 2 === 0);
        const isOdd = sorted.every((v) => v % 2 !== 0);
        if (count === 3 && isEven) {
          return 'rolling an even number';
        }
        if (count === 3 && isOdd) {
          return 'rolling an odd number';
        }
        const isLte = (n: number) => sorted.length === n && sorted.every((v, i) => v === i + 1);
        const isGt = (n: number) => sorted.length === 6 - n && sorted.every((v, i) => v === n + 1 + i);
        if (isLte(3)) {
          return 'rolling 3 or less';
        }
        if (isGt(3)) {
          return 'rolling greater than 3';
        }
        if (isLte(2)) {
          return 'rolling 2 or less';
        }
        if (isGt(4)) {
          return 'rolling greater than 4';
        }
        return null;
      }

      case 'basketball':
        return selectedLabels[0] === 'Make' ? 'making the shot' : 'missing the shot';

      case 'lottery':
        return selectedLabels[0] === 'Win (+$18)' ? 'winning the lottery' : 'not winning';

      default:
        return null;
    }
  }

  private runSingleExperiment(): void {
    if (!this.hasSelectedOutcomes()) {
      return;
    }
    if (this.maxRunsReached()) {
      return;
    }

    const exp = this.selectedExperiment();
    const selected = this.targetOutcomes();
    const n = this.trials();
    const currentRuns = this.runs();
    const runNumber = currentRuns.length + 1;

    const newResults: ExperimentTrialResult[] = [];
    for (let i = 0; i < n; i++) {
      const outcome = this.sampleOutcome(exp.outcomes);
      newResults.push({
        trialNumber: i + 1,
        outcome,
        isSuccess: selected[exp.outcomes.indexOf(outcome)],
      });
    }

    const successCount = newResults.filter((r) => r.isSuccess).length;
    const run: ExperimentRun = {
      runNumber,
      trials: n,
      results: newResults,
      successCount,
      lossCount: n - successCount,
    };

    this.runs.set([...currentRuns, run]);

    // Keep flat results for the inline first-run display
    const existing = this.results();
    const indexedResults = newResults.map((r, i) => ({ ...r, trialNumber: existing.length + i + 1 }));
    this.results.set([...existing, ...indexedResults]);
    this.visibleCount.set(existing.length + indexedResults.length);
    this.scrollRunsToBottom();
  }

  private pFracLatex(): string {
    const outcomes = this.selectedExperiment().outcomes;
    const selected = this.targetOutcomes();
    let numSum = 0;
    const den = outcomes[0].probabilityDenominator;
    for (let i = 0; i < outcomes.length; i++) {
      if (selected[i]) {
        numSum += outcomes[i].probabilityNumerator;
      }
    }
    const frac = numSum === 1 && den === 1 ? '1' : `\\frac{${numSum}}{${den}}`;
    return `\\textcolor{${COLOR_P}}{${frac}}`;
  }

  private qFracLatex(): string {
    const outcomes = this.selectedExperiment().outcomes;
    const selected = this.targetOutcomes();
    let numSum = 0;
    const den = outcomes[0].probabilityDenominator;
    for (let i = 0; i < outcomes.length; i++) {
      if (selected[i]) {
        numSum += outcomes[i].probabilityNumerator;
      }
    }
    const qNum = den - numSum;
    return qNum === 1 && den === 1 ? '1' : `\\frac{${qNum}}{${den}}`;
  }

  private getBinomialVerb(): string {
    switch (this.selectedType()) {
      case 'coin':
        return 'flip the coin';
      case 'die':
        return 'roll the die';
      case 'basketball':
        return 'shoot free throws';
      case 'lottery':
        return 'play the lottery';
      default:
        return 'run the experiment';
    }
  }

  private sampleOutcome(outcomes: readonly OutcomeDefinition[]): OutcomeDefinition {
    const denominator = outcomes[0].probabilityDenominator;
    const rand = Math.random() * denominator;
    let cumulative = 0;
    for (const outcome of outcomes) {
      cumulative += outcome.probabilityNumerator;
      if (rand < cumulative) {
        return outcome;
      }
    }
    return outcomes[outcomes.length - 1];
  }

  private getExperimentVerb(): string {
    switch (this.selectedType()) {
      case 'coin':
        return 'Flipping';
      case 'die':
        return 'Rolling';
      case 'basketball':
        return 'Shooting';
      case 'lottery':
        return 'Playing';
      default:
        return 'Running';
    }
  }

  private getExperimentNoun(): string {
    switch (this.selectedType()) {
      case 'coin':
        return 'coin';
      case 'die':
        return 'die';
      case 'basketball':
        return 'basket';
      case 'lottery':
        return 'lottery';
      default:
        return 'experiment';
    }
  }

  private getOutcomeSvgMarkup(svgType: string): string {
    const raw = OUTCOME_SVG_MAP[svgType] ?? '';
    return `<span class="inline-outcome-svg">${raw}</span>`;
  }

  private scrollRunsToBottom(): void {
    const el = this.runsScrollContainer()?.nativeElement;
    if (el) {
      const scrollY = window.scrollY;
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY });
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      });
    }
  }
}
